import { EducationStatus } from '@novaheal/types';
import {
  FieldPath,
  FieldValues,
  PathValue,
  UseFormRegisterReturn,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';
import { PREFILLED_EXAMINATION_DATE_FIELD_NAMES } from './examinationDate';

export type RevalidateExaminationAfterStatusChangeContext<T extends FieldValues> = {
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
  shouldShowExaminationFields: (status: string) => boolean;
  /** Clears month/year without triggering validation — `trigger` runs after. */
  clearExaminationFields?: () => void;
};

/**
 * Updates `educationStatus` and runs one Joi pass so exam month/year are
 * re-checked against the new Lehrdrittel window (see `examinationYearSchema`).
 *
 * Use with {@link pickSelectFieldRegistration} so SelectField does not also
 * fire `register().onChange` (avoids racing validation passes).
 */
export const revalidateExaminationAfterStatusChange = async <T extends FieldValues>(
  newStatus: EducationStatus,
  ctx: RevalidateExaminationAfterStatusChangeContext<T>
): Promise<void> => {
  const { setValue, trigger, shouldShowExaminationFields, clearExaminationFields } = ctx;

  setValue('educationStatus' as FieldPath<T>, newStatus as PathValue<T, FieldPath<T>>, {
    shouldDirty: true,
    shouldTouch: true,
    shouldValidate: false,
  });

  if (!shouldShowExaminationFields(newStatus)) {
    setValue('examinationDateUnknown' as FieldPath<T>, false as PathValue<T, FieldPath<T>>, {
      shouldValidate: false,
    });
    clearExaminationFields?.();
  }

  // Include month/year so resolver clears stale errors when fields were hidden/cleared
  // (RHF only updates errors for triggered fields).
  await trigger([
    'educationStatus',
    ...PREFILLED_EXAMINATION_DATE_FIELD_NAMES,
  ] as FieldPath<T>[]).catch(() => undefined);
};

/**
 * Omit `onChange` when the parent supplies its own handler (e.g. status change revalidation).
 */
export const pickSelectFieldRegistration = (
  registration: UseFormRegisterReturn
): Pick<UseFormRegisterReturn, 'name' | 'ref' | 'onBlur' | 'disabled'> => {
  const { name, ref, onBlur, disabled } = registration;
  return { name, ref, onBlur, disabled };
};
